export const scalaDefaultSource = `import scala.collection.mutable.{ArrayBuffer, HashMap, ListBuffer}

object MutableCollectionsExample {
  def processData(): Unit = {
    // Mutable collections usage
    val buffer = new ArrayBuffer[String]()
    buffer += "Scala"
    buffer += "Akka"
    buffer += "Play"
    
    val scores = new HashMap[String, Int]()
    scores("Alice") = 95
    scores("Bob") = 87
    scores("Charlie") = 92
    
    // More mutable collections
    val items = ListBuffer[String]()
    items.append("Item 1")
    items.append("Item 2")
    
    // Already using immutable collections
    val immutableList = List(1, 2, 3, 4)
    val immutableMap = Map("key1" -> "value1", "key2" -> "value2")
  }
  
  def transformData(data: ArrayBuffer[Int]): Unit = {
    // In-place mutation
    for (i <- 0 until data.length) {
      data(i) = data(i) * 2
    }
    
    // Conditionally add elements
    if (data.length < 10) {
      data += 100
      data += 200
    }
  }
}`;

export const scalaDefaultCodemod = `import { type SgRoot } from "@ast-grep/napi";

async function transform(root: SgRoot): Promise<string | null> {
  const rootNode = root.root();

  // Replace mutable ArrayBuffer declarations with immutable List or Vector
  const arrayBufferNodes = rootNode.findAll({
    rule: {
      pattern: "val $NAME = new ArrayBuffer[$TYPE]()"
    }
  });

  let edits = arrayBufferNodes.map(node => {
    const name = node.getMatch("NAME").text();
    const type = node.getMatch("TYPE").text();
    return node.replace(\`val \${name} = Vector[\${type}]()\`);
  });

  // Replace ListBuffer with immutable List or Vector
  const listBufferNodes = rootNode.findAll({
    rule: {
      pattern: "val $NAME = ListBuffer[$TYPE]()"
    }
  });

  edits = [
    ...edits,
    ...listBufferNodes.map(node => {
      const name = node.getMatch("NAME").text();
      const type = node.getMatch("TYPE").text();
      return node.replace(\`val \${name} = Vector[\${type}]()\`);
    })
  ];

  // Replace mutable HashMap declarations with immutable Map
  const hashMapNodes = rootNode.findAll({
    rule: {
      pattern: "val $NAME = new HashMap[$KEY_TYPE, $VALUE_TYPE]()"
    }
  });

  edits = [
    ...edits,
    ...hashMapNodes.map(node => {
      const name = node.getMatch("NAME").text();
      const keyType = node.getMatch("KEY_TYPE").text();
      const valueType = node.getMatch("VALUE_TYPE").text();
      return node.replace(\`val \${name} = Map[\${keyType}, \${valueType}]()\`);
    })
  ];

  // Replace mutable append operations with immutable operations
  const appendNodes = rootNode.findAll({
    rule: {
      pattern: "$COLLECTION += $VALUE"
    }
  });

  edits = [
    ...edits,
    ...appendNodes.map(node => {
      const collection = node.getMatch("COLLECTION").text();
      const value = node.getMatch("VALUE").text();
      return node.replace(\`\${collection} = \${collection} :+ \${value}\`);
    })
  ];

  // Apply all changes
  const newSource = rootNode.commitEdits(edits);
  return newSource;
}

export default transform;
`;

export const scalaDefaultConfig = `
# Scala mutable to immutable
id: array-buffer
language: scala
rule:
  pattern: "val $NAME = new ArrayBuffer[$TYPE]()"
fix: "val $NAME = Vector[$TYPE]()"
---
id: list-buffer
language: scala
rule:
  pattern: "val $NAME = ListBuffer[$TYPE]()"
fix: "val $NAME = Vector[$TYPE]()"
---
id: hash-map
language: scala
rule:
  pattern: "val $NAME = new HashMap[$KEY_TYPE, $VALUE_TYPE]()"
fix: "val $NAME = Map[$KEY_TYPE, $VALUE_TYPE]()"
---
id: plus-equals
language: scala
rule:
  pattern: "$COLLECTION += $VALUE"
fix: "$COLLECTION = $COLLECTION :+ $VALUE"
---
id: property-access
language: scala
rule:
  pattern: "$COLLECTION($KEY) = $VALUE"
fix: "$COLLECTION = $COLLECTION + ($KEY -> $VALUE)"
`.trim();
